<template>
  <div class="kanban-board">
    <div v-if="loading" class="kanban-loading">Loading board…</div>
    <div v-else-if="error" class="kanban-error">{{ error }}</div>
    <div v-else class="kanban-columns">
      <div v-for="col in columns" :key="col.id" class="kanban-column">
        <h3 class="kanban-column-header">{{ col.title }} ({{ col.cards.length }})</h3>
        <div class="kanban-cards">
          <div
            v-for="card in col.cards"
            :key="card.milestoneId"
            class="kanban-card"
            :class="cardClass(card)"
          >
            <strong>{{ card.title }}</strong>
            <div class="kanban-card-project">{{ card.projectName }}</div>
            <div v-if="card.dueOn" class="kanban-card-due">Due: {{ card.dueOn }}</div>
            <span v-if="card.overdue" class="kanban-card-overdue">OVERDUE</span>
            <span v-if="card.paymentReady" class="kanban-card-payment">Payment ready</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

const props = defineProps<{ url: string; moveUrl?: string }>()

interface Card {
  milestoneId: number
  title: string
  status: string
  projectName: string
  projectId: number
  dueOn: string | null
  overdue: boolean
  signoffStatus: string | null
  paymentReady: boolean
}

interface Column {
  id: string
  title: string
  cards: Card[]
}

const columns = ref<Column[]>([])
const loading = ref(true)
const error = ref<string | null>(null)

function cardClass(card: Card): string {
  const classes = [`status-${card.status}`]
  if (card.overdue) classes.push('overdue')
  if (card.paymentReady) classes.push('payment-ready')
  return classes.join(' ')
}

onMounted(async () => {
  try {
    const response = await fetch(props.url, {
      headers: { 'Accept': 'application/json' },
      credentials: 'same-origin'
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    columns.value = await response.json()
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to load board'
  } finally {
    loading.value = false
  }
})
</script>
